import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitBranch, Loader2, Plus, Trash2, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { cn } from '../lib/cn';
import { MailingIntestazione } from '../components/email/MailingIntestazione';
import { useNotifications } from '../store/useStore';
import { automationService, type EtichettaScelta } from '../lib/email/automationService';
import { SceltaPartenza } from '../components/email/automazioni/SceltaPartenza';
import { SceltaTipoSequenza } from '../components/email/automazioni/SceltaTipoSequenza';
import { descriviPartenza, partenzaCompleta, type Partenza } from '../lib/email/partenzaSequenza';
import { AUTOMATION_STATUS, type EmailAutomation } from '../types/automazioni.types';

export default function EmailAutomationsPage() {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();

  const [sequenze, setSequenze] = useState<EmailAutomation[]>([]);
  const [caricando, setCaricando] = useState(true);

  const [nuovaAperta, setNuovaAperta] = useState(false);
  const [nome, setNome] = useState('');
  // "A chi della lista" parte vuoto apposta: fra scrivere a tutta la lista e
  // scrivere solo ai nuovi c'e' troppa differenza per lasciarlo a un valore
  // di partenza.
  const [partenza, setPartenza] = useState<Partenza>({
    trigger: 'contatto_attivato',
    listaId: '',
    chi: null,
  });
  const [servizio, setServizio] = useState(false);
  const [liste, setListe] = useState<EtichettaScelta[]>([]);
  const [creando, setCreando] = useState(false);

  const nomiListe = useMemo(() => Object.fromEntries(liste.map((l) => [l.id, l.name])), [liste]);

  const carica = useCallback(async () => {
    try {
      const [elenco, tutteLeListe] = await Promise.all([
        automationService.elenco(),
        automationService.etichette(),
      ]);
      setSequenze(elenco);
      setListe(tutteLeListe);
    } catch (errore) {
      addNotification({
        type: 'error',
        title: 'Non riesco a leggere le sequenze',
        message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
      });
    } finally {
      setCaricando(false);
    }
  }, [addNotification]);

  useEffect(() => {
    carica();
  }, [carica]);

  const crea = async () => {
    if (!nome.trim() || !partenzaCompleta(partenza)) return;
    setCreando(true);
    try {
      const nuova = await automationService.crea(nome.trim(), partenza, servizio);
      navigate(`/mailing/sequenze/${nuova.id}`);
    } catch (errore) {
      addNotification({
        type: 'error',
        title: 'Non riesco a creare la sequenza',
        message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
      });
      setCreando(false);
    }
  };

  const elimina = async (sequenza: EmailAutomation) => {
    await automationService.elimina(sequenza.id);
    setSequenze((s) => s.filter((x) => x.id !== sequenza.id));
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <MailingIntestazione descrizione="Un percorso che il contatto fa da solo: manda, aspetta, guarda cos'e' successo, manda ancora.">
        <Button
          onClick={() => setNuovaAperta(true)}
          className="h-10 px-4 shadow-lg shadow-primary-500/20 bg-primary-600 hover:bg-primary-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuova sequenza
        </Button>
      </MailingIntestazione>

      {caricando ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        </div>
      ) : sequenze.length === 0 ? (
        <Card className="border-gray-200 border-dashed">
          <CardContent className="py-14 text-center">
            <GitBranch className="w-10 h-10 text-neutral-400 mx-auto mb-3 opacity-30" />
            <p className="text-sm text-neutral-600 font-medium">Nessuna sequenza</p>
            <p className="text-xs text-neutral-400 mt-1 max-w-md mx-auto leading-relaxed">
              La prima che conviene fare e' il benvenuto: chi si iscrive riceve una email subito,
              e dopo qualche giorno la seconda se non ha cliccato.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {sequenze.map((sequenza) => {
            const stato = AUTOMATION_STATUS[sequenza.status];
            return (
              <Card
                key={sequenza.id}
                className="border-gray-200 hover:border-primary-300 transition-colors cursor-pointer group"
                onClick={() => navigate(`/mailing/sequenze/${sequenza.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <span className={cn('w-1 h-10 rounded-full flex-shrink-0', stato.accento)} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {sequenza.name}
                      </p>
                      <p className="text-[11px] text-neutral-400 mt-0.5 truncate">
                        {sequenza.servizio && (
                          <span className="font-semibold text-primary-700">Info corso · </span>
                        )}
                        {descriviPartenza(sequenza, nomiListe)}
                      </p>
                    </div>
                    <span
                      className={cn(
                        'text-[11px] px-2 py-0.5 rounded-md border font-medium flex-shrink-0',
                        stato.badge
                      )}
                    >
                      {stato.label}
                    </span>
                  </div>

                  <p className="text-[11px] text-neutral-400 mt-2.5 leading-relaxed">
                    {stato.spiegazione}
                  </p>

                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                    <span className="inline-flex items-center gap-1.5 text-xs text-neutral-500">
                      <Users className="w-3.5 h-3.5" />
                      {sequenza.entrati_count} entrati · {sequenza.finiti_count} arrivati in fondo
                    </span>
                    {sequenza.status === 'bozza' && (
                      <button
                        type="button"
                        title="Elimina"
                        onClick={(e) => {
                          e.stopPropagation();
                          elimina(sequenza);
                        }}
                        className="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-red-600 transition-all"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Nuova sequenza */}
      <Dialog open={nuovaAperta} onOpenChange={setNuovaAperta}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuova sequenza</DialogTitle>
            <DialogDescription>
              Prima si decide perche' un contatto ci entra. Il percorso si disegna dopo.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="nome-sequenza" className="text-xs">
                Come si chiama
              </Label>
              <Input
                id="nome-sequenza"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Benvenuto ai nuovi iscritti"
                className="mt-1"
              />
            </div>

            <SceltaTipoSequenza servizio={servizio} onChange={setServizio} />
            <SceltaPartenza valore={partenza} liste={liste} servizio={servizio} onChange={setPartenza} />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNuovaAperta(false)} disabled={creando}>
              Annulla
            </Button>
            <Button
              onClick={crea}
              disabled={!nome.trim() || !partenzaCompleta(partenza) || creando}
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              {creando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Crea e disegna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
